import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Map } from "immutable";
import { Spin, message } from "antd";
import {
    fetchCaseDetail,
    fetchCaseDocumentChecklist,
    fetchCompanyAssociatesV2,
    fetchCompanyProfile,
    updateCompanyBankAccounts,
    fetchFacilityAccount,
    fetchCompanyDocuments,
    fetchDocumentTypes,
    fetchCaseEnachMandateStatus,
    fetchFacilityAccountSupplyChains,
    updateProcessingFee,
    fetchAssociatesVerificationList
} from "store/actions";
import { PermissionContextManager } from "hocs/permission-context-manager";
import { UI_PERMISSIONS, STATE_STATUS } from "constants/index";
import DocumentsService from "services/documents-service";
import CompanyService from "services/company-service";
import FacilityAccountService from "services/facility-account-service";
import "styles/ops-workspace.css";

import { SECTIONS, NAV_ITEMS, DOC_FILTER_ALL, DOC_STATUS, CHECKLIST_STATE } from "./constants";
import { Icon, ICONS, Pill, SectionErrorBoundary } from "./ui";
import SummaryBand from "./summary-band";
import ChecklistDrawer from "./checklist-drawer";
import CaseReferenceSection from "./case-reference-section";
import DocumentsSection from "./documents-section";
import AccountFacilitySection from "./account-facility-section";
import NachSection from "./nach-section";
import AgreementSection from "./agreement-section";
import RequestDocsModal from "./request-docs-modal";
import {
    buildApplicants,
    buildBankAccounts,
    buildCompanyAddresses,
    buildDocuments,
    buildDocumentChecklist,
    buildFormChecklist,
    toAssociatePayload,
    validateAssociatePayload,
    validateApplicantRoles,
    validateShareholding,
    get,
    money,
    toArray
} from "./data-utils";

/**
 * Ops Workspace — one surface for everything a post-sanction operator has to
 * clear before the PS Kit can be triggered.
 *
 * It does not introduce any new backend: every panel reads the same endpoint
 * the corresponding standalone tab reads (Documents Review, Account/Facility
 * Details, NACH Mandate, Agreement, Doc Checklist), so the two never disagree.
 * What it adds is the cross-cutting view — the PS Kit validation band at the
 * top, which reconciles the form checklist (derived from the applicant record
 * and its PAN / Aadhaar / GST cross-checks) with the document checklist
 * (`/case/<id>/checklist/`) and tells the operator exactly what is left.
 */
class OpsWorkspace extends React.Component {
    state = {
        section: SECTIONS.OVERVIEW,
        docView: "review",
        docFilter: DOC_FILTER_ALL,
        selectedDocId: null,
        checklistOpen: false,
        requestDocsOpen: false,
        requestingDocs: false,
        requestDocsPreset: [],
        // Bumped after re-trigger reasons are saved so the embedded Agreement
        // screen remounts and re-reads its logs.
        agreementKey: 0,
        busy: false,
        // Non-redux slices fetched directly (same services the sibling tabs use).
        emiSchedule: [],
        charges: {},
        loanTerms: null
    };

    componentDidMount() {
        this.loadReduxSlices();
        this.loadFacilityExtras();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.caseId !== this.props.caseId) {
            this.setState({ emiSchedule: [], charges: {}, loanTerms: null });
            this.loadReduxSlices();
            this.loadFacilityExtras();
        }
    }

    componentWillUnmount() {
        if (this.lmsTimer) window.clearTimeout(this.lmsTimer);
    }

    loadReduxSlices() {
        const { caseId, companyId } = this.props;
        if (caseId) {
            this.props.fetchCaseDocumentChecklist(caseId);
            this.props.fetchCompanyAssociatesV2(caseId);
            this.props.fetchCaseEnachMandateStatus(caseId);
            this.props.fetchFacilityAccountSupplyChains(caseId);
            this.props.fetchFacilityAccount(caseId);
        }
        if (companyId) {
            // Addresses and bank accounts live on the company profile — the
            // standalone fetchCompanyAddresses / fetchCompanyBankAccounts
            // actions have no saga behind them, so this is the only call that
            // actually populates them.
            this.props.fetchCompanyProfile(companyId, caseId);
            this.props.fetchCompanyDocuments(caseId, companyId);
            this.props.fetchAssociatesVerificationList(companyId);
        }
        if (this.props.docMasterListStatus === STATE_STATUS.UNFETCHED) {
            this.props.fetchDocumentTypes();
        }
    }

    loadFacilityExtras() {
        const { caseId } = this.props;
        if (!caseId) return;
        FacilityAccountService.getBNPLlistingV2(caseId)
            .then(({ data }) => {
                const schedule = (data && data.emi_schedule) || [];
                this.setState({
                    emiSchedule: schedule.map(row => ({
                        instalment_nbr: row.instalment_nbr,
                        emi_amount: row.aggregate_emi_amount || row.emi_amount,
                        principal_amount: row.aggregate_principal_amount || row.principal_amount,
                        interest_amount: row.aggregate_interest_amount || row.interest_amount,
                        principal_receivable:
                            row.aggregate_principal_receivable || row.principal_receivable,
                        interest_paid_till_date:
                            row.aggregate_interest_paid_till_date || row.interest_paid_till_date,
                        tentative_due_date: row.tentative_due_date,
                        upfront_interest_amount:
                            row.aggregate_upfront_interest_amount || row.upfront_interest_amount
                    }))
                });
            })
            .catch(() => this.setState({ emiSchedule: [] }));

        FacilityAccountService.getScheduleOfChargesBNPL(caseId)
            .then(({ data }) => this.setState({ charges: (data && data.data) || {} }))
            .catch(() => this.setState({ charges: {} }));

        FacilityAccountService.getSavedSchedule(caseId)
            .then(({ data }) =>
                this.setState({
                    loanTerms: data
                        ? {
                            principal: data.principal,
                            tenure: data.tenure,
                            emi_type: data.emi_type,
                            moratorium_days: data.moratorium_days,
                            tentative_disbursal_date: data.tentative_disbursal_date,
                            is_first_instalment_deducted_from_disbursal:
                                  data.is_first_instalment_deducted_from_disbursal
                        }
                        : null
                })
            )
            .catch(() => this.setState({ loanTerms: null }));
    }

    /* ------------------------------------------------------------ derived */

    applicants() {
        return buildApplicants(this.props.associatesV2);
    }

    documents(applicants) {
        return buildDocuments(
            this.props.files,
            applicants,
            get(this.props.company, "name")
        );
    }

    docTypeOptions() {
        return toArray(this.props.docMasterList)
            .map(item => ({
                value: get(item, "id"),
                label: get(item, "document_name")
            }))
            .filter(option => option.value && option.label)
            .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }

    /* ------------------------------------------------------------ actions */

    go = section => this.setState({ section });

    /** Checklist row -> the section where the operator fixes it. */
    goToChecklistRow = row =>
        this.setState({
            checklistOpen: false,
            section: row.field ? SECTIONS.OVERVIEW : SECTIONS.DOCUMENTS
        });

    // Re-trigger reasons were saved to the PS Kit log; remount the Agreement
    // screen so its Logs table shows the new entry.
    onPsKitRetriggered = () =>
        this.setState(state => ({ agreementKey: state.agreementKey + 1 }));

    sectionLabel() {
        const item = NAV_ITEMS.find(nav => nav.key === this.state.section);
        return item ? item.label : "This section";
    }

    refreshDocuments() {
        const { caseId, companyId } = this.props;
        this.props.fetchCompanyDocuments(caseId, companyId);
        this.props.fetchCaseDocumentChecklist(caseId);
        this.props.fetchCaseDetail(caseId);
    }

    refreshVerification = () => {
        if (this.props.companyId) {
            this.props.fetchAssociatesVerificationList(this.props.companyId);
        }
    };

    /**
     * Re-read everything the Account / Facility panel is built from after one
     * of its writes. The case record carries the fee program, sanction
     * condition and LMS flags; the three service calls carry the schedule and
     * the schedule of charges.
     */
    refreshFacility = () => {
        const { caseId } = this.props;
        if (!caseId) return;
        this.props.fetchCaseDetail(caseId);
        this.props.fetchFacilityAccount(caseId);
        this.props.fetchFacilityAccountSupplyChains(caseId);
        this.loadFacilityExtras();
    };

    /**
     * The LOS -> LMS proceed checklist, evaluated exactly as the Account /
     * Facility Details screen evaluates it so the same case passes (or fails)
     * from either surface.
     */
    losChecklist(applicants, documents) {
        const { caseDetail, company } = this.props;
        const PS_KIT_DOCUMENT_IDS = [18, 19, 20, 56];
        return [
            {
                label: "Updated Applicants Details",
                ok: applicants.every(
                    applicant =>
                        applicant.fields.phone &&
                        applicant.fields.first_name &&
                        applicant.fields.email
                )
            },
            {
                label: "Is PS-Kit Attached",
                ok: documents.some(doc =>
                    PS_KIT_DOCUMENT_IDS.includes(Number(doc.documentId))
                )
            },
            {
                label: "Processing Fee Added",
                ok: get(caseDetail, "prepaid_processing_fee")
                    ? !!get(caseDetail, "is_processing_fee_paid")
                    : true
            },
            {
                label: "Seller Details",
                ok: !!get(caseDetail, "is_seller_key_details_available")
            },
            {
                label: "Seller Scheme Added",
                ok: !!(
                    get(caseDetail, "is_scheme_added") &&
                    get(caseDetail, "is_scheme_dwd_sp_added")
                )
            },
            { label: "Fee Program Added", ok: !!get(caseDetail, "is_fee_available") },
            {
                label: "Associate CIBIL",
                // The screen checks `!Number.isNaN(doesAllApplicantsHaveCibil)`
                // on a boolean, so this row always passes there. Kept as-is:
                // the workspace must not gate the case harder than the tab.
                ok: !Number.isNaN(company && company.doesAllApplicantsHaveCibil)
            },
            {
                label: "Withhold Account for Seller Added",
                ok: !!get(caseDetail, "is_withhold_account_added")
            },
            {
                label: "Disbursal Account for Seller Added",
                ok: !!get(caseDetail, "is_disbursal_account_added")
            }
        ];
    }

    /** POST `case/<id>/los-to-lms/` — the same payload the screen sends. */
    proceedToLms = () => {
        const onCase = get(this.props.caseDetail, "processing_fee_amount");
        const perChain = toArray(this.props.supplyChains)
            .map(chain => Number(get(chain, "processing_fee_amount")))
            .filter(value => !isNaN(value));
        const amount =
            onCase !== null && onCase !== undefined
                ? parseFloat(onCase)
                : perChain.length
                    ? Math.max.apply(null, perChain)
                    : 0;
        this.props.updateProcessingFee(this.props.caseId, amount);
        // The saga owns the success / failure message; re-read once it has had
        // time to land so the LMS flag and the button state follow.
        this.lmsTimer = window.setTimeout(this.refreshFacility, 1500);
    };

    /**
     * One write path for every document change. `document_status` moves the
     * file through the review states; `associate` moves it between
     * company- and applicant-level. Mirrors what the Documents Review tab does
     * on approve, so the same backend side effects (identity validation,
     * Aadhaar masking) still fire.
     */
    writeDocument = (doc, { documentId, associateId, status, isActive, requestRemarks, fileName }) => {
        const { caseId, companyId } = this.props;
        const nextDocument = documentId !== undefined ? documentId : doc.documentId;
        const nextAssociate = associateId !== undefined ? associateId : doc.associateId;
        const nextStatus = status !== undefined ? status : doc.status;
        const nextActive = isActive !== undefined ? isActive : true;

        // Retiring a file, renaming it, or leaving a request against it does
        // not need a document type; everything else does.
        if (
            !nextDocument &&
            nextActive &&
            requestRemarks === undefined &&
            fileName === undefined
        ) {
            message.warning("Tag a document type first");
            return Promise.resolve();
        }

        this.setState({ busy: true });
        return DocumentsService.validateDocument(companyId, doc.id, {
            id: doc.id,
            file_name: fileName !== undefined ? fileName : doc.name,
            document: nextDocument,
            document_option: doc.documentOption || null,
            document_status: nextStatus,
            is_active: nextActive,
            remarks: doc.remarks,
            parse_data: get(doc.raw, "parse_data"),
            request_remarks:
                requestRemarks !== undefined
                    ? requestRemarks
                    : get(doc.raw, "request_remarks"),
            case_id: caseId
        })
            .then(() => {
                if (!nextActive || Number(nextStatus) !== DOC_STATUS.VERIFIED) return null;
                // Verifying also (re)links the file to the case at the chosen
                // document type / owner.
                return DocumentsService.attachDocument({
                    case: caseId,
                    company: companyId,
                    file: doc.id,
                    document: nextDocument,
                    document_option: doc.documentOption || null,
                    associate: nextAssociate || null,
                    document_status: nextStatus,
                    is_valid: true
                }).catch(() => null);
            })
            .then(() => {
                message.success("Document updated");
                this.refreshDocuments();
            })
            .catch(() => message.error("Could not update the document"))
            .finally(() => this.setState({ busy: false }));
    };

    /** Rename a file — the Document Review list's inline file-name edit. */
    onRenameDocument = (doc, fileName) => this.writeDocument(doc, { fileName });

    /**
     * Archive files off the case, or restore them. `company/remove-documents/`
     * flips their status rather than deleting anything, which is why the
     * archive stays browsable.
     */
    onArchiveDocuments = (ids, action) =>
        DocumentsService.actionDuplicateDocuments(ids, action)
            .then(() => {
                message.success(
                    action === "restore" ? "Documents restored" : "Documents archived"
                );
                this.refreshDocuments();
            })
            .catch(() =>
                message.error(
                    action === "restore"
                        ? "Could not restore the documents"
                        : "Could not archive the documents"
                )
            );

    openRequestDocs = () => this.setState({ requestDocsOpen: true, requestDocsPreset: [] });

    /** Checklist document row -> Request modal with that document ticked. */
    requestChecklistDoc = row =>
        this.setState({
            requestDocsOpen: true,
            requestDocsPreset: row.documentId ? [String(row.documentId)] : []
        });

    closeRequestDocs = () => this.setState({ requestDocsOpen: false });

    /**
     * Request documents from the customer through the same two calls
     * Document Review's request saga makes (see buildDocRequest).
     */
    submitRequestDocs = ({ docs, postDocs }) => {
        const { companyId, caseId } = this.props;
        const call = postDocs
            ? DocumentsService.requestPostSanctionDocuments
            : DocumentsService.requestDocuments;
        this.setState({ requestingDocs: true });
        return call(companyId, docs, caseId)
            .then(() => {
                message.success("Documents requested");
                this.setState({ requestingDocs: false, requestDocsOpen: false, docView: "requested" });
                this.refreshDocuments();
            })
            .catch(() => {
                message.error("Could not request the documents");
                this.setState({ requestingDocs: false });
            });
    };

    /** Ask the customer for this document again, with a reason. */
    onRequestDocument = (doc, remarks) =>
        this.writeDocument(doc, { requestRemarks: remarks });

    /**
     * Upload files under a chosen document type (and applicant), through the
     * same `bulk-file-add` call Document Review's upload form uses.
     */
    onUploadDocument = (values, done) => {
        const { companyId, caseId } = this.props;
        const formData = new FormData();
        (values.files || []).forEach(file => formData.append("files", file));
        if (caseId) formData.append("case", caseId);
        this.setState({ busy: true });
        return DocumentsService.uploadBulkDocuments(
            companyId,
            formData,
            values.document_type,
            values.associate
        )
            .then(() => {
                message.success("Document uploaded");
                this.refreshDocuments();
                done(true);
            })
            .catch(() => {
                message.error("Could not upload the document");
                done(false);
            })
            .finally(() => this.setState({ busy: false }));
    };

    /** Email the customer the outstanding document list. */
    onNotifyCustomer = () =>
        DocumentsService.sendDocumentNotification(this.props.caseId)
            .then(() => message.success("Notification sent"))
            .catch(() => message.error("Could not send the notification"));

    onRetagDocument = (doc, documentId) =>
        this.writeDocument(doc, { documentId });

    onReassignDocument = (doc, associateId) =>
        this.writeDocument(doc, { associateId: associateId || null });

    onApproveDocument = doc =>
        this.writeDocument(doc, { status: DOC_STATUS.VERIFIED });

    onRejectDocument = doc =>
        this.writeDocument(doc, { status: DOC_STATUS.PENDING });

    /**
     * Full applicant save — every field on the card in one POST to
     * `company/case/<id>/associate-and-address-detail/`. That is the call
     * Case Details \u25b8 Applicants makes, and the only one that accepts the
     * fields the narrow associate endpoint leaves out (marital status,
     * profession, shareholding, annual income, GST / partnership-deed names,
     * alternate and office phone).
     *
     * The endpoint only touches the associates named in the body, so sending
     * just the edited one is safe.
     */
    onSaveApplicant = (applicant, draft, done) => {
        const { caseId, companyId, company } = this.props;
        const payload = toAssociatePayload(applicant, draft, { companyId });

        const roleError = validateApplicantRoles(this.applicants(), applicant.id, draft);
        if (roleError) {
            message.error(roleError);
            done(false);
            return;
        }

        const fieldError = validateAssociatePayload(payload);
        if (fieldError) {
            message.error(fieldError);
            done(false);
            return;
        }
        const shareError = validateShareholding(
            this.applicants(),
            get(company, "constitution"),
            applicant.id,
            payload.shareholding
        );
        if (shareError) {
            message.error(shareError);
            done(false);
            return;
        }

        this.setState({ busy: true });
        CompanyService.updateCompanyAssociatesV2([payload], caseId)
            .then(() => {
                message.success("Applicant updated");
                this.props.fetchCompanyAssociatesV2(caseId);
                this.props.fetchCompanyProfile(companyId, caseId);
                this.props.fetchCaseDocumentChecklist(caseId);
                done(true);
            })
            .catch(error => {
                message.error(
                    (error && (error.message || error.non_field_errors)) ||
                        "Could not update the applicant"
                );
                done(false);
            })
            .finally(() => this.setState({ busy: false }));
    };

    /**
     * Address save — applicant or company, same endpoint and the same
     * AddAddressForm payload shaping Credit Checker uses. This is the only
     * path that can mint a new city / district / state master row.
     */
    onSaveAddress = (formValues, done) => {
        const { caseId, companyId } = this.props;
        CompanyService.updateSingleAddress(companyId, formValues, caseId)
            .then(() => {
                message.success("Address updated");
                this.props.fetchCompanyProfile(companyId, caseId);
                this.props.fetchCompanyAssociatesV2(caseId);
                this.props.fetchCaseDocumentChecklist(caseId);
                done(true);
            })
            .catch(() => {
                message.error("Could not update the address");
                done(false);
            });
    };

    /**
     * Bank account save. Goes through the `updateCompanyBankAccounts` action
     * rather than the service directly, so the reducer update and case-detail
     * refetch behave exactly as they do on Case Details and Credit Checker.
     * Takes one record or several (the key-account switch demotes the old and
     * promotes the new in a single write).
     */
    onSaveBankAccount = (records, done) => {
        const { caseId, companyId } = this.props;
        const list = Array.isArray(records) ? records : [records];
        this.props.updateCompanyBankAccounts(Number(companyId), list, Number(caseId));
        done(true);
        // The action is a saga; give it a beat before re-reading the profile.
        setTimeout(() => this.props.fetchCompanyProfile(companyId, caseId), 1200);
    };

    /* ------------------------------------------------------------- render */

    renderHeader(applicants, documents) {
        const { caseDetail, company, caseId } = this.props;
        const schemeType = get(caseDetail, "scheme_type");
        const deferral = get(caseDetail, "deferral_tagged");
        return (
            <div className={"ops-ws__header"}>
                <div className={"ops-ws__title"}>
                    <span className={"ops-ws__title-name"}>
                        {get(company, "name") || "—"}
                    </span>
                    <span className={"ops-ws__title-code"}>#{caseId}</span>
                </div>
                <div className={"ops-ws__tags"}>
                    {schemeType ? <Pill tone={"info"}>{schemeType}</Pill> : null}
                    {this.props.stageName ? (
                        <Pill tone={"neutral"}>{this.props.stageName}</Pill>
                    ) : null}
                    {deferral ? <Pill tone={"warn"}>Deferral open</Pill> : null}
                    <Pill tone={documents.length ? "neutral" : "warn"}>
                        {documents.filter(d => Number(d.status) === DOC_STATUS.VERIFIED).length}
                        /{documents.length} docs verified
                    </Pill>
                    <Pill tone={"neutral"}>
                        {applicants.length} applicant{applicants.length === 1 ? "" : "s"}
                    </Pill>
                </div>
                <div className={"ops-ws__spacer"} />
                <div className={"ops-ws__limit"}>
                    <span className={"ops-ws__limit-label"}>
                        {get(caseDetail, "sanction_limit") ? "Sanction Limit" : "Proposed Limit"}
                    </span>
                    <span className={"ops-ws__limit-value"}>
                        {money(
                            get(caseDetail, "sanction_limit") || get(caseDetail, "proposed_limit")
                        )}
                    </span>
                </div>
            </div>
        );
    }

    hasTermLoan() {
        const caseDetail = this.props.caseDetail;
        return (
            this.state.emiSchedule.length > 0 ||
            !!get(caseDetail, "is_bnpl_case") ||
            /term\s*loan/i.test(String(get(caseDetail, "scheme_type") || ""))
        );
    }

    renderRail(documents, nachReady, kitSigned) {
        const { section } = this.state;
        const verified = documents.filter(
            d => Number(d.status) === DOC_STATUS.VERIFIED
        ).length;
        const badges = {
            [SECTIONS.OVERVIEW]: null,
            [SECTIONS.DOCUMENTS]: `${verified}/${documents.length}`,
            [SECTIONS.ACCOUNT]: this.hasTermLoan() ? "TL" : "SCF",
            [SECTIONS.NACH]: nachReady ? "Ready" : "Open",
            [SECTIONS.AGREEMENT]: kitSigned ? "Signed" : "Pending"
        };

        const steps = [
            {
                label: "Documents finalised",
                detail: `${verified} of ${documents.length} verified`,
                done: documents.length > 0 && verified === documents.length
            },
            {
                label: "NACH confirmed",
                detail: nachReady ? "Mandates verified" : "Mandate pending",
                done: nachReady
            },
            {
                label: "PS Kit signed",
                detail: kitSigned ? "All signatories done" : "Awaiting signatures",
                done: kitSigned
            }
        ];

        return (
            <div className={"ops-ws__rail"}>
                <div>
                    <div className={"ops-ws__rail-title"}>Ops Workspace</div>
                    <div className={"ops-ws__nav"}>
                        {NAV_ITEMS.map(item => (
                            <button
                                key={item.key}
                                type={"button"}
                                className={
                                    "ops-ws__nav-item" + (section === item.key ? " is-active" : "")
                                }
                                onClick={() => this.go(item.key)}
                            >
                                <span className={"ops-ws__nav-icon"}>
                                    <Icon d={item.icon} size={17} />
                                </span>
                                <span className={"ops-ws__nav-text"}>
                                    <span className={"ops-ws__nav-label"}>{item.label}</span>
                                    <span className={"ops-ws__nav-sub"} style={{ display: "block" }}>
                                        {item.sub}
                                    </span>
                                </span>
                                {badges[item.key] ? (
                                    <Pill tone={"info"}>{badges[item.key]}</Pill>
                                ) : null}
                            </button>
                        ))}
                    </div>
                </div>

                <div className={"ops-ws__steps"}>
                    <div className={"ops-ws__rail-title"}>Critical path</div>
                    {steps.map((step, index) => (
                        <div key={step.label} className={"ops-ws__step"}>
                            <div className={"ops-ws__step-gutter"}>
                                <span
                                    className={
                                        "ops-ws__step-dot" + (step.done ? " is-done" : " is-active")
                                    }
                                >
                                    {step.done ? (
                                        <Icon d={ICONS.check} size={11} stroke={"#fff"} width={3.4} />
                                    ) : (
                                        <span
                                            style={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: "50%",
                                                background: "#fff",
                                                display: "block"
                                            }}
                                        />
                                    )}
                                </span>
                                {index < steps.length - 1 ? (
                                    <span className={"ops-ws__step-line"} />
                                ) : null}
                            </div>
                            <div className={"ops-ws__step-body"}>
                                <div className={"ops-ws__step-label"}>{step.label}</div>
                                <div className={"ops-ws__step-detail"}>{step.detail}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    render() {
        const { caseDetail, checklist, filesStatus, associatesV2Status } = this.props;
        const loading =
            filesStatus === STATE_STATUS.FETCHING ||
            associatesV2Status === STATE_STATUS.FETCHING;

        const applicants = this.applicants();
        const documents = this.documents(applicants);
        const addresses = buildCompanyAddresses(this.props.addresses);
        const banks = buildBankAccounts(this.props.bankAccounts);

        const formRows = applicants.reduce(
            (rows, applicant) => rows.concat(buildFormChecklist(applicant)),
            []
        );
        const docRows = buildDocumentChecklist(
            checklist,
            documents,
            get(this.props.company, "constitution")
        );
        const pendingOf = rows =>
            rows.filter(
                row => row.mandatory && row.status !== CHECKLIST_STATE.COMPLETE
            );
        const formPending = pendingOf(formRows);
        const docPending = pendingOf(docRows);

        const nachProceed =
            (this.props.nachStatus &&
                this.props.nachStatus.get &&
                this.props.nachStatus.get("proceed_validation")) ||
            null;
        const nachReady = !!(
            nachProceed &&
            (nachProceed.get ? nachProceed.get("can_proceed") : nachProceed.can_proceed)
        );
        // The Agreement screen owns its own data; the workspace only needs the
        // case-level flag for the rail badge and the readiness list. It must
        // not fetch the agreement itself — GET /legality/v3/legality/ creates a
        // CaseLegality row as a side effect, so a second caller racing the
        // embedded screen was blanking its status, signatories and dates.
        const kitSigned = !!get(caseDetail, "is_ps_kit_signed");

        const readiness = [
            {
                label: "All documents verified",
                ok:
                    documents.length > 0 &&
                    documents.every(d => Number(d.status) === DOC_STATUS.VERIFIED),
                value: `${documents.filter(d => Number(d.status) === DOC_STATUS.VERIFIED).length} / ${documents.length}`
            },
            {
                label: "Mandatory documents on file",
                ok: docPending.length === 0,
                value: docPending.length ? `${docPending.length} open` : "Done"
            },
            {
                label: "Applicant details complete",
                ok: formPending.length === 0,
                value: formPending.length ? `${formPending.length} open` : "Done"
            },
            { label: "NACH confirmed", ok: nachReady, value: nachReady ? "Confirmed" : "Pending" },
            { label: "PS Kit signed", ok: kitSigned, value: kitSigned ? "Signed" : "Pending" }
        ];

        // Editing is gated on the documents_edit permission. Whether the case
        // task is currently assigned to this user is surfaced as a warning
        // instead of a hard block — see the banner below.
        const canEdit = !!this.props.DOCUMENTS_EDIT;
        const unassigned = canEdit && !this.props.isUserAllowedToEdit;

        if (loading && !documents.length && !applicants.length) {
            return (
                <div className={"ops-ws"}>
                    <div className={"ops-ws__spin"}>
                        <Spin size={"large"} />
                    </div>
                </div>
            );
        }

        return (
            <div className={"ops-ws"}>
                {this.renderHeader(applicants, documents)}

                <div className={"ops-ws__body"}>
                    {this.renderRail(documents, nachReady, kitSigned)}

                    <div className={"ops-ws__main"}>
                        <SummaryBand
                            formPending={formPending}
                            docPending={docPending}
                            onOpenChecklist={() => this.setState({ checklistOpen: true })}
                            onGoForm={() => this.go(SECTIONS.OVERVIEW)}
                            onGoDocs={() => this.go(SECTIONS.DOCUMENTS)}
                        />

                        {!canEdit || unassigned ? (
                            <div
                                className={"ops-ws__note"}
                                style={{ marginTop: 14, display: "flex", gap: 9, alignItems: "flex-start" }}
                            >
                                <Icon d={ICONS.alert} size={15} stroke={"#b54708"} />
                                <span>
                                    {canEdit
                                        ? "This case task is not currently assigned to you. You can still save changes here — take care not to overwrite someone else's work."
                                        : "View only — you do not have the documents_edit permission on this case."}
                                </span>
                            </div>
                        ) : null}

                        <div style={{ height: 18 }} />

                        <SectionErrorBoundary name={this.sectionLabel()}>
                            {this.state.section === SECTIONS.OVERVIEW ? (
                                <CaseReferenceSection
                                    applicants={applicants}
                                    addresses={addresses}
                                    banks={banks}
                                    caseId={this.props.caseId}
                                    documents={documents}
                                    verification={toArray(this.props.verificationList).filter(
                                        row =>
                                            row &&
                                            applicants.some(
                                                applicant =>
                                                    Number(applicant.id) ===
                                                    Number(row.associate_id)
                                            )
                                    )}
                                    verificationLoading={
                                        this.props.verificationStatus === STATE_STATUS.FETCHING
                                    }
                                    onRefreshVerification={this.refreshVerification}
                                    companyId={this.props.companyId}
                                    associateList={this.props.companyAssociates}
                                    canEdit={canEdit}
                                    busy={this.state.busy}
                                    onSaveApplicant={this.onSaveApplicant}
                                    onSaveAddress={this.onSaveAddress}
                                    onSaveBankAccount={this.onSaveBankAccount}
                                />
                            ) : null}

                            {this.state.section === SECTIONS.DOCUMENTS ? (
                                <DocumentsSection
                                    documents={documents}
                                    applicants={applicants}
                                    companyName={get(this.props.company, "name")}
                                    docTypeOptions={this.docTypeOptions()}
                                    view={this.state.docView}
                                    onViewChange={docView => this.setState({ docView })}
                                    filter={this.state.docFilter}
                                    onFilterChange={docFilter => this.setState({ docFilter })}
                                    selectedId={this.state.selectedDocId}
                                    onSelect={selectedDocId => this.setState({ selectedDocId })}
                                    canEdit={canEdit}
                                    busy={this.state.busy}
                                    readiness={readiness}
                                    onRetag={this.onRetagDocument}
                                    onReassign={this.onReassignDocument}
                                    onApprove={this.onApproveDocument}
                                    onReject={this.onRejectDocument}
                                    onRequest={this.onRequestDocument}
                                    onRequestNew={this.openRequestDocs}
                                    onUpload={this.onUploadDocument}
                                    onNotify={this.onNotifyCustomer}
                                    onRename={this.onRenameDocument}
                                    onArchive={this.onArchiveDocuments}
                                    stageId={this.props.stageId}
                                />
                            ) : null}

                            {this.state.section === SECTIONS.ACCOUNT ? (
                                <AccountFacilitySection
                                    caseId={this.props.caseId}
                                    caseDetail={caseDetail}
                                    facility={this.props.facility}
                                    supplyChains={this.props.supplyChains}
                                    emiSchedule={this.state.emiSchedule}
                                    charges={this.state.charges || {}}
                                    loanTerms={this.state.loanTerms}
                                    hasTermLoan={this.hasTermLoan()}
                                    stageId={this.props.stageId}
                                    user={this.props.user}
                                    recreditViewOnly={this.props.recreditViewOnlyPermission}
                                    lmsChecklist={this.losChecklist(applicants, documents)}
                                    onProceedToLms={this.proceedToLms}
                                    onRefresh={this.refreshFacility}
                                />
                            ) : null}

                            {this.state.section === SECTIONS.NACH ? (
                                <NachSection
                                    nachStatus={this.props.nachStatus}
                                    bankAccounts={banks}
                                    caseId={this.props.caseId}
                                    companyId={this.props.companyId}
                                    schemeType={this.props.schemeType}
                                    caseDetail={caseDetail}
                                />
                            ) : null}

                            {this.state.section === SECTIONS.AGREEMENT ? (
                                <AgreementSection
                                    key={this.state.agreementKey}
                                    caseId={this.props.caseId}
                                    location={this.props.location}
                                    stageId={this.props.stageId}
                                    caseDetail={caseDetail}
                                    company={this.props.company}
                                    user={this.props.user}
                                    postSanctionPermission={this.props.postSanctionPermission}
                                    permissionContext={this.props.permissionContext}
                                />
                            ) : null}
                        </SectionErrorBoundary>
                    </div>
                </div>

                <RequestDocsModal
                    open={this.state.requestDocsOpen}
                    onClose={this.closeRequestDocs}
                    onSubmit={this.submitRequestDocs}
                    docMasterList={this.props.docMasterList}
                    preset={this.state.requestDocsPreset}
                    applicants={applicants}
                    busy={this.state.requestingDocs}
                />

                <ChecklistDrawer
                    open={this.state.checklistOpen}
                    onClose={() => this.setState({ checklistOpen: false })}
                    formRows={formRows}
                    docRows={docRows}
                    caseId={this.props.caseId}
                    canRetrigger={!!this.props.postSanctionPermission}
                    onGoToRow={this.goToChecklistRow}
                    onRetriggered={this.onPsKitRetriggered}
                    canEdit={canEdit}
                    onRequestDoc={this.requestChecklistDoc}
                />
            </div>
        );
    }
}

OpsWorkspace.propTypes = {
    caseId: PropTypes.number,
    companyId: PropTypes.number,
    stageName: PropTypes.string,
    stageId: PropTypes.number,
    companyName: PropTypes.string,
    schemeType: PropTypes.string,
    location: PropTypes.object,
    user: PropTypes.object,
    postSanctionPermission: PropTypes.bool,
    permissionContext: PropTypes.object,
    isUserAllowedToEdit: PropTypes.bool,
    DOCUMENTS_EDIT: PropTypes.bool,
    caseDetail: PropTypes.object,
    company: PropTypes.object,
    checklist: PropTypes.object,
    associatesV2: PropTypes.object,
    associatesV2Status: PropTypes.string,
    addresses: PropTypes.object,
    bankAccounts: PropTypes.object,
    companyAssociates: PropTypes.object,
    facility: PropTypes.object,
    files: PropTypes.object,
    filesStatus: PropTypes.string,
    docMasterList: PropTypes.any,
    docMasterListStatus: PropTypes.string,
    nachStatus: PropTypes.object,
    supplyChains: PropTypes.any,
    fetchCaseDetail: PropTypes.func.isRequired,
    fetchCaseDocumentChecklist: PropTypes.func.isRequired,
    fetchCompanyAssociatesV2: PropTypes.func.isRequired,
    fetchCompanyProfile: PropTypes.func.isRequired,
    updateCompanyBankAccounts: PropTypes.func.isRequired,
    fetchFacilityAccount: PropTypes.func.isRequired,
    fetchCompanyDocuments: PropTypes.func.isRequired,
    fetchDocumentTypes: PropTypes.func.isRequired,
    fetchCaseEnachMandateStatus: PropTypes.func.isRequired,
    fetchFacilityAccountSupplyChains: PropTypes.func.isRequired,
    updateProcessingFee: PropTypes.func.isRequired,
    fetchAssociatesVerificationList: PropTypes.func.isRequired,
    verificationList: PropTypes.object,
    verificationStatus: PropTypes.string,
    recreditViewOnlyPermission: PropTypes.bool
};

// Different slices of the case-details store are keyed by the raw prop and by
// its Number() form depending on which action wrote them, so read both.
const caseSlice = (state, caseId, path) =>
    state.caseDetails.getIn([caseId].concat(path)) ||
    state.caseDetails.getIn([Number(caseId)].concat(path));

const mapStateToProps = (state, props) => ({
    caseDetail: caseSlice(state, props.caseId, ["data"]),
    checklist: caseSlice(state, props.caseId, ["checklist", "data"]),
    nachStatus: caseSlice(state, props.caseId, ["nachStatusResponse"]) || Map(),
    supplyChains:
        state.facilityAccount.getIn(["supply_chains", props.caseId, "data"]) ||
        state.facilityAccount.getIn([
            "supply_chains",
            Number(props.caseId),
            "data"
        ]),
    company: props.companyId
        ? state.companies.getIn([props.companyId, "data"])
        : null,
    associatesV2: state.companies.get("associatesListV2", Map({})),
    associatesV2Status: state.companies.get("associatesListV2Status"),
    addresses: state.companies.getIn([props.companyId, "data", "addresses"], Map({})),
    bankAccounts: state.companies.getIn(
        [props.companyId, "data", "bank_accounts"],
        Map({})
    ),
    // AddAssociateBankAccountForm wants the company's associates as an
    // Immutable collection of AssociateRecord (it reads each one's `option`).
    companyAssociates: state.companies.getIn(
        [props.companyId, "data", "associates"],
        Map({})
    ),
    facility: state.facilityAccount.get("data"),
    files: state.documents
        .getIn(["company_documents", props.companyId, "data"], Map({}))
        .map(_ => _.get("data"))
        .filter(_ => _ && _.is_active)
        .sort((a, b) => (a.modified_date && b.modified_date && a.modified_date.isBefore(b.modified_date) ? 1 : -1))
        .toList(),
    filesStatus: state.documents.getIn(
        ["company_documents", props.companyId, "status"],
        STATE_STATUS.UNFETCHED
    ),
    verificationList: state.applicantVerificationList.getIn([
        "companies",
        props.companyId,
        "data"
    ]),
    verificationStatus: state.applicantVerificationList.getIn([
        "companies",
        props.companyId,
        "status"
    ]),
    docMasterList: state.documents.getIn(["documentsMasterList", "data"]),
    docMasterListStatus: state.documents.getIn([
        "documentsMasterList",
        "status"
    ])
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            fetchCaseDetail,
            fetchCaseDocumentChecklist,
            fetchCompanyAssociatesV2,
            fetchCompanyProfile,
            updateCompanyBankAccounts,
            fetchFacilityAccount,
            fetchCompanyDocuments,
            fetchDocumentTypes,
            fetchCaseEnachMandateStatus,
            fetchFacilityAccountSupplyChains,
            updateProcessingFee,
            fetchAssociatesVerificationList
        },
        dispatch
    );

export default PermissionContextManager({
    permission: UI_PERMISSIONS.DOCUMENTS_VIEW,
    propsPermissions: {
        DOCUMENTS_EDIT: UI_PERMISSIONS.DOCUMENTS_EDIT
    }
})(connect(mapStateToProps, mapDispatchToProps)(OpsWorkspace));
